import { MapPin, User } from 'lucide-react';
import type { MapData, MapTerritory, TerritoryType } from '../../types';

interface TerritorySidebarProps {
  mapData: MapData | null;
  selectedRegionId: string | null;
}

const BOUNDARY_TYPE_LABELS: Record<TerritoryType, string> = {
  STATE: 'State',
  ZIP_CODE: 'Zip Code',
  HYBRID: 'Hybrid',
};

const BOUNDARY_TYPE_COLORS: Record<TerritoryType, string> = {
  STATE: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  ZIP_CODE: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  HYBRID: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
};

function TerritoryCard({ territory, regionColor }: { territory: MapTerritory; regionColor: string }) {
  const stateCount = territory.states.length;
  const zipCount = territory.zipCodes.length;

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
            style={{ backgroundColor: regionColor }}
          />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{territory.name}</span>
        </div>
        <span
          className={`ml-2 flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
            BOUNDARY_TYPE_COLORS[territory.boundaryType]
          }`}
        >
          {BOUNDARY_TYPE_LABELS[territory.boundaryType]}
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
        <User className="h-3 w-3 flex-shrink-0" />
        <span className={territory.assignedRep ? 'text-gray-700 dark:text-gray-300' : 'italic text-gray-400 dark:text-gray-500'}>
          {territory.assignedRep?.displayName ?? 'Unassigned'}
        </span>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        {stateCount > 0 && (
          <span>
            {stateCount} state{stateCount !== 1 ? 's' : ''}
          </span>
        )}
        {zipCount > 0 && (
          <span>
            {zipCount} zip prefix{zipCount !== 1 ? 'es' : ''}
          </span>
        )}
        {stateCount === 0 && zipCount === 0 && <span className="italic">No boundaries</span>}
      </div>
    </div>
  );
}

export default function TerritorySidebar({ mapData, selectedRegionId }: TerritorySidebarProps) {
  const selectedRegion = mapData?.regions.find((r) => r.id === selectedRegionId) ?? null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          Territories
        </h3>
        {selectedRegion && (
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: selectedRegion.color }}
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">{selectedRegion.name}</span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {!selectedRegionId ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <MapPin className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Select a region on the map</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">or click a legend item above</p>
          </div>
        ) : !selectedRegion ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Region not found</div>
        ) : selectedRegion.territories.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4 italic">No territories in this region</div>
        ) : (
          <div className="space-y-3">
            {selectedRegion.territories.map((territory) => (
              <TerritoryCard
                key={territory.id}
                territory={territory}
                regionColor={selectedRegion.color}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
